import { Card, CardBody, Col, Input } from 'reactstrap';
import moment from 'moment';
import './TravelRequestOptions.scss';
import { useCallback, useEffect, useState } from 'react';
import { toFixedTwoDecimal } from '../../../utils/number.util';

function HotelOptions({
  requestOptions,
  onSelectHotelOption,
  showTitle = false,
  status = '',
  isView = false,
}) {
  // Retrieve Details
  const { headerDetails, hotelOptions, hotel2Options, hotel3Options } =
    requestOptions || {};
  // Selected Hotel Option
  const [selectedHotelOption, setSelectedHotelOption] = useState(
    requestOptions?.headerDetails?.Selected_Option_Hotel || null,
  );
  // Check for empty array
  const isArrayNotEmpty = arr => Array.isArray(arr) && arr.length > 0;
  // Gather all hotel options
  const allHotelOptions = [hotelOptions, hotel2Options, hotel3Options];
  // Gather all quote values
  const quotes = [
    { value: headerDetails?.Option1_Hotel_QuoteValue },
    { value: headerDetails?.Option2_Hotel_QuoteValue },
    { value: headerDetails?.Option3_Hotel_QuoteValue },
  ];
  // Get quote with lowest quote value
  let minIndex = -1;
  let minValue = Infinity;

  quotes.forEach((q, i) => {
    if (allHotelOptions[i]?.length) {
      const val = Number(q?.value);
      if (val < minValue) {
        minValue = val;
        minIndex = i;
      }
    }
  });

  const handleSelectHotelOption = useCallback(
    optionIndex => {
      setSelectedHotelOption(optionIndex);
      const isLowest = optionIndex === minIndex + 1;

      if (onSelectHotelOption) {
        onSelectHotelOption(optionIndex, isLowest);
      }
    },
    [minIndex, onSelectHotelOption],
  );

  useEffect(() => {
    if (status === 'Pending for Decision') {
      handleSelectHotelOption(minIndex + 1);
    }
  }, [minIndex, status, handleSelectHotelOption]);

  const getBackgroundColor = index => {
    if (index === minIndex + 1) {
      return '#ECFDF3';
    }
    return '#f0f9ff';
  };

  const getDefaultBackgroundColor = index => {
    if (index === minIndex + 1) {
      return '#ECFDF3';
    }
    return '#ffffff';
  };

  const displayValue = (v, fallback = '-') => {
    if (v == null) return fallback;
    if (typeof v === 'string') {
      const t = v.trim();
      return t === '' ? fallback : t;
    }
    return v;
  };

  return (
    <>
      {showTitle && (
        <h6 className="text-sm fw-semibold">Hotel Booking Option</h6>
      )}

      <div className="flight-options-container">
        {allHotelOptions.map((option, index) => {
          const noRecord = Object.values(option).some(
            item => item === null || item === undefined || item === '',
          );
          const seq = index + 1;

          return (
            <Card
              className="trd-flight-selection cursor-pointer"
              onClick={() => !isView && handleSelectHotelOption(index + 1)}
              style={{
                background:
                  selectedHotelOption === index + 1
                    ? getBackgroundColor(index + 1)
                    : getDefaultBackgroundColor(index + 1),
                borderColor: minIndex === index ? '#47CD89' : '#98a2b3',
              }}
              key={index}
            >
              <CardBody>
                <div className="d-flex flex-row align-items-center gap-2 mb-3">
                  <div>
                    <Input
                      type="radio"
                      name="hotel-selection"
                      disabled={noRecord}
                      checked={selectedHotelOption === index + 1}
                      onChange={() =>
                        !isView && handleSelectHotelOption(index + 1)
                      }
                    />
                  </div>
                  <div className="trd-flight-selection-label">
                    Hotel Option - {seq}
                  </div>
                </div>
                {isArrayNotEmpty(option) ? (
                  <>
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex flex-row align-items-center justify-content-between">
                        <strong>Quote Value</strong>
                        <div>
                          {headerDetails?.[`Option${seq}_Hotel_Curr`]}
                          {toFixedTwoDecimal(
                            headerDetails?.[`Option${seq}_Hotel_QuoteValue`],
                          )}
                        </div>
                      </div>
                      <div className="d-flex flex-row align-items-center justify-content-between">
                        <strong>Rate Per Night</strong>
                        <div>
                          {headerDetails?.[`Option${seq}_Hotel_Curr`]}
                          {toFixedTwoDecimal(
                            headerDetails?.[`Option${seq}_Hotel_RatesPerNight`],
                          )}
                        </div>
                      </div>
                      <div className="d-flex flex-row align-items-start justify-content-between">
                        <Col>
                          <strong>Terms</strong>
                        </Col>
                        <Col className="d-flex justify-content-end">
                          <div>
                            <p className="trd-terms">
                              {displayValue(
                                headerDetails?.[`Option${seq}_Hotel_Terms`],
                              )}
                            </p>
                          </div>
                        </Col>
                      </div>
                    </div>
                    <div className="trd-flight-separator-header" />
                    {option?.map((citem, cindex) => (
                      <>
                        <div className="d-flex flex-column">
                          <div className="d-flex flex-row align-items-start justify-content-between">
                            <strong>Hotel</strong>
                            <div>
                              <p className="trd-terms">{citem?.HotelName}</p>
                            </div>
                          </div>
                          <div className="d-flex flex-row align-items-start justify-content-between">
                            <strong>Place</strong>
                            <div>
                              <p className="trd-terms">{citem?.Place}</p>
                            </div>
                          </div>
                          <div className="d-flex flex-row align-items-start justify-content-between">
                            <strong>Check In</strong>
                            <div>
                              <p className="trd-terms">
                                {citem?.CheckInDate
                                  ? moment(citem.CheckInDate).format('L')
                                  : 'N/A'}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex flex-row align-items-start justify-content-between">
                            <strong>Check Out</strong>
                            <div>
                              {citem?.CheckOutDate
                                ? moment(citem.CheckOutDate).format('L')
                                : 'N/A'}
                            </div>
                          </div>
                        </div>
                        {cindex !== (option?.length ?? 1) - 1 && (
                          <div className="trd-flight-separator mt-2" />
                        )}
                      </>
                    ))}
                  </>
                ) : (
                  <span>No hotel option found.</span>
                )}
              </CardBody>
            </Card>
          );
        })}
      </div>
    </>
  );
}

export default HotelOptions;
